import React, { useState, useEffect, useRef } from "react";

// Native <input type="date"> forces users to scroll a year picker one
// click at a time — painfully slow for a date of birth decades in the
// past. This renders a plain text field the user types into directly
// (DD/MM/YYYY), auto-inserting the slashes, while still emitting the
// same "YYYY-MM-DD" value the rest of the app already expects.

const isoToDigits = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${d}${mo}${y}`;
};

const digitsToDisplay = (digits) => {
  const d = digits.slice(0, 2);
  const mo = digits.slice(2, 4);
  const y = digits.slice(4, 8);
  let out = d;
  if (mo) out += `/${mo}`;
  if (y) out += `/${y}`;
  return out;
};

const digitsToIso = (digits) => {
  if (digits.length !== 8) return "";
  const d = digits.slice(0, 2);
  const mo = digits.slice(2, 4);
  const y = digits.slice(4, 8);
  return `${y}-${mo}-${d}`;
};

export default function DateOfBirthInput({
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  className,
  style,
  disabled,
  required,
  id,
  placeholder = "OO/UU/AAAA",
}) {
  const [digits, setDigits] = useState(() => isoToDigits(value));
  const lastEmittedIso = useRef(value || "");

  // Stay in sync when the parent loads/resets a value we didn't just emit
  // ourselves (e.g. opening a record to edit).
  useEffect(() => {
    if ((value || "") !== lastEmittedIso.current) {
      setDigits(isoToDigits(value));
      lastEmittedIso.current = value || "";
    }
  }, [value]);

  const handleChange = (e) => {
    const nextDigits = e.target.value.replace(/\D/g, "").slice(0, 8);
    setDigits(nextDigits);
    const iso = digitsToIso(nextDigits);
    lastEmittedIso.current = iso;
    onChange?.({ target: { name, value: iso, type: "text" } });
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      name={name}
      value={digitsToDisplay(digits)}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      maxLength={10}
      disabled={disabled}
      required={required}
      className={className}
      style={style}
    />
  );
}
